import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsMissionControlHomepageAndBetaFeatureFlagOn,
} from 'core/userContext';
import { CampaignIdBadge } from 'modules/campaigns/lib/components/CampaignIdBadge';
import {
  InheritedConversionSource,
  useInheritedConversionInfo,
} from 'modules/campaigns/lib/hooks/useInheritedConversionInfo';
import {
  BasePaddedFormSectionContent,
  FlexColumnHalfGapFormSectionContent,
  HalfPaddedFormSectionContent,
  RadioButton,
  RadioLabel,
  RadioOption,
  SmallInputWrapper,
  StyledLeftTooltip,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import {
  getJourneyCurrentConversion,
  getJourneyCurrentConversionEvent,
} from 'modules/workflows/ducks';
import { AttributionMode, BackendJourneyConversion } from 'modules/workflows/types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputText } from 'ui/inputs';
import { Tooltip } from 'ui/tooltip';
import {
  ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR,
  ATTRIBUTION_MODE_LOOKUP,
  DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS,
  DEFAULT_ATTRIBUTION_PERIOD,
  MAX_ATTRIBUTION_PERIOD,
  REVERSE_ATTRIBUTION_MODE_LOOKUP,
} from '../../BaseSendEditor.constants';
import { useCampaign } from '../../BaseSendEditor.hooks';
import { validateMultiAttributionPeriod as validate } from '../../BaseSendEditor.utils';
import MultiConversionDropdown from './MultiConversionDropdown';
import MultiConversionPeriodInput from './MultiConversionPeriodInput';
import MultiConversionRadios from './MultiConversionRadios';
import {
  AttributePeriodRow,
  AttributePeriodRowContainer,
  ErrorText,
  ErrorWrapper,
  RadioOptionRow,
  StyledWarningBanner,
  Subtitle2Bold,
} from './MultipleConversionSelection.styles';
import {
  getJourneyGoalAttributionPeriodHours,
  getModeNameFromConversions,
  getMultiselectValues,
  getPeriodFromConversions,
  getSelectedValueNamesAsTooltip,
  updateAttributionModeConfig,
} from './MultipleConversionSelection.utils';

export interface MultipleConversionSelectionProps {
  isDisabled?: boolean;
}

export const MULTIPLE_CONVERSIONS_TEST_ID = 'multiple-conversions';

export const MultipleConversionSelection = ({
  isDisabled = false,
}: MultipleConversionSelectionProps) => {
  const {
    campaign = {},
    updateCampaign,
    cloneErrors = {},
    cloneDisplayValues = {},
  } = useCampaign();
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isMissionControlAndBetaEnabled = useSelector(
    selectIsMissionControlHomepageAndBetaFeatureFlagOn,
  );
  const activeJourneyGoal = useSelector(getJourneyCurrentConversion);
  const journeyConversion = useSelector(getJourneyCurrentConversionEvent);

  const { conversions = [] } = campaign;

  const defaultMode = isJourneyGoalsEnabled
    ? AttributionMode.AttributionPeriod
    : AttributionMode.ExplicitlySet;
  const multiselectValues = useMemo(() => getMultiselectValues(conversions), [conversions]);
  const mode = useMemo(
    () => getModeNameFromConversions(conversions) || defaultMode,
    [conversions, defaultMode],
  );
  const journeyGoalsPeriod =
    isJourneyGoalsEnabled && !!activeJourneyGoal
      ? getJourneyGoalAttributionPeriodHours(journeyConversion)
      : undefined;
  const period = useMemo(
    () => journeyGoalsPeriod ?? getPeriodFromConversions(conversions) ?? 24,
    [journeyGoalsPeriod, conversions],
  );

  const [periodError, setPeriodError] = useState(validate(period as number));

  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>();

  const handleConversionSelection = (values: string[]) => {
    const updatedConversions = values.map((value) => ({
      events: [{ name: value, attributionMode: { name: mode, periodHours: period } }],
    }));
    updateCampaign({ conversions: updatedConversions });
  };

  const handlePeriodUpdate = (updatedPeriod: number) => {
    const updatedConversions = updateAttributionModeConfig(conversions, {
      periodHours: updatedPeriod,
    });
    updateCampaign({ conversions: updatedConversions });
  };

  const handleAttributionsModeUpdate = (updatedMode: AttributionMode) => {
    const updatedConversions = updateAttributionModeConfig(conversions, {
      name: REVERSE_ATTRIBUTION_MODE_LOOKUP[updatedMode],
      ...(updatedMode === AttributionMode.AttributionPeriod && {
        periodHours: period || DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS,
      }),
    });
    if (updatedMode === AttributionMode.ExplicitlySet) {
      handlePeriodUpdate(Number(DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS));
      setPeriodError(validate(DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS as number));
    }
    updateCampaign({ conversions: updatedConversions });
  };

  const addConversion = (updatedEvent: string[], updatedMode: string, updatedPeriod: number) => {
    const updatedConversions = updatedEvent.map((value) => ({
      events: [
        {
          name: value,
          attributionMode: {
            name: updatedMode as AttributionMode,
            periodHours: updatedPeriod || DEFAULT_ATTRIBUTION_MODE_PERIOD_HOURS,
          },
        },
      ],
    }));
    updateCampaign({ conversions: updatedConversions });
  };

  useEffect(() => {
    if (multiselectValues.length) {
      setTooltipContent(getSelectedValueNamesAsTooltip(multiselectValues));
    }
  }, [multiselectValues]);

  /*
   * When we first load the journey studio, we look to see if we have any journey conversions
   * configured. If so we will push them into the campaign to be saved, if there's already a conversion in place from
   * the campaign, we do nothing. This is to ensure that the journey conversion is always attached the a newly created campaign.
   */
  useEffect(() => {
    if (isJourneyGoalsEnabled) {
      const event = journeyConversion?.eventName;
      const campaignHasJourneyConversion = campaign?.conversions?.some(
        (conversion) => conversion.events?.[0]?.name === event,
      );
      if (event && !campaignHasJourneyConversion) {
        const attributionMode = journeyConversion?.attributionMode;
        const attributionPeriodHours = journeyConversion?.attributionPeriodHours;

        addConversion([...multiselectValues, event], attributionMode, attributionPeriodHours);
      }
    }
  }, []);

  const journeyGoals: BackendJourneyConversion[] = useMemo(
    () => [activeJourneyGoal].filter((goal): goal is BackendJourneyConversion => !!goal),
    [activeJourneyGoal],
  );

  const { attributionModesDisabledTooltipContent, isAttributionModesDisabled } =
    useInheritedConversionInfo({
      projectGoals: [], // TODO: Update this once the backend is ready. Feel free to use `MOCK_PROJECT_GOALS` for testing
      journeyGoals,
      source: InheritedConversionSource.JourneyStudio,
    });

  const isAttributionInputDisabled =
    isDisabled ||
    isAttributionModesDisabled ||
    ATTRIBUTION_MODE_LOOKUP[mode] !== AttributionMode.AttributionPeriod;

  const isAttributionRadioButtonDisabled = isDisabled || isAttributionModesDisabled;

  return (
    <HalfPaddedFormSectionContent data-test={MULTIPLE_CONVERSIONS_TEST_ID}>
      <Subtitle2Bold>Conversion events</Subtitle2Bold>
      <BasePaddedFormSectionContent>
        <StyledLeftTooltip
          isEnabled={isDisabled && !!multiselectValues.length}
          tooltipContent={tooltipContent}
        >
          <MultiConversionDropdown
            values={multiselectValues}
            onChange={handleConversionSelection}
            isDisabled={isDisabled}
            isError={cloneErrors.conversions}
          />
        </StyledLeftTooltip>
      </BasePaddedFormSectionContent>
      {cloneErrors.conversions && (
        <StyledWarningBanner title="Some referenced events are missing in current project">
          <ul>
            {(cloneDisplayValues.conversions as string[])?.map((val) => (
              <li key={val}>{val}</li>
            ))}
          </ul>
        </StyledWarningBanner>
      )}
      {!!conversions.length && (
        <>
          {
            /**
             * TODO: We now have a RadioGroup component that can be used here instead of this custom implementation.
             * @see https://main--5f3c2484e2a3200022307192.chromatic.com/?path=/story/components-radio-group--interactive&args=isBordered:!false
             */
            isJourneyGoalsEnabled || isMissionControlAndBetaEnabled ? (
              <>
                <HalfPaddedFormSectionContent>
                  <FlexColumnHalfGapFormSectionContent>
                    <Tooltip
                      isEnabled={isAttributionModesDisabled}
                      tooltipContent={attributionModesDisabledTooltipContent}
                    >
                      <RadioOption
                        key={`option-${ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod.value}`}
                      >
                        <RadioOptionRow>
                          <RadioButton
                            id={ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod.value}
                            value={
                              ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod.value
                            }
                            isChecked={
                              mode ===
                              ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod.value
                            }
                            onClick={() =>
                              handleAttributionsModeUpdate(
                                ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod.value,
                              )
                            }
                            isDisabled={isAttributionRadioButtonDisabled}
                          />
                          <AttributePeriodRowContainer>
                            <AttributePeriodRow>
                              <RadioLabel
                                isDisabled={isAttributionRadioButtonDisabled}
                                onClick={() =>
                                  handleAttributionsModeUpdate(
                                    ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod
                                      .value,
                                  )
                                }
                              >
                                {
                                  ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.AttributionPeriod
                                    .prefix
                                }
                              </RadioLabel>

                              <SmallInputWrapper id="attr-period-wrapper">
                                <InputText
                                  hasError={periodError?.hasError}
                                  id="attr-period"
                                  data-test="attr-period"
                                  // TODO: fix the getting of html number attrs in input text
                                  // @ts-ignore
                                  max={MAX_ATTRIBUTION_PERIOD}
                                  onBlur={() => setPeriodError(validate(period as number))}
                                  onChange={(periodString: string) =>
                                    handlePeriodUpdate(Number(periodString))
                                  }
                                  placeholder={period ? '' : `${DEFAULT_ATTRIBUTION_PERIOD}`}
                                  Suffix={
                                    <span>{`${Number(period) === 1 ? 'hour' : 'hours'}`}</span>
                                  }
                                  type="number"
                                  value={
                                    ATTRIBUTION_MODE_LOOKUP[mode] !==
                                    AttributionMode.AttributionPeriod
                                      ? '24'
                                      : `${period}`
                                  }
                                  isDisabled={isAttributionInputDisabled}
                                />
                              </SmallInputWrapper>
                            </AttributePeriodRow>
                          </AttributePeriodRowContainer>
                        </RadioOptionRow>
                      </RadioOption>
                    </Tooltip>

                    {periodError.hasError && (
                      <ErrorWrapper>
                        <ErrorText>{periodError.message}</ErrorText>
                      </ErrorWrapper>
                    )}

                    <Tooltip
                      isEnabled={isAttributionModesDisabled}
                      tooltipContent={attributionModesDisabledTooltipContent}
                    >
                      <RadioOption
                        key={`option-${ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value}`}
                      >
                        <RadioButton
                          id={ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value}
                          value={ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value}
                          isChecked={
                            mode === ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value
                          }
                          onClick={() =>
                            handleAttributionsModeUpdate(
                              ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value,
                            )
                          }
                          isDisabled={isAttributionRadioButtonDisabled}
                        />
                        <RadioLabel
                          onClick={() =>
                            handleAttributionsModeUpdate(
                              ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.value,
                            )
                          }
                          isDisabled={isAttributionRadioButtonDisabled}
                        >
                          {ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.prefix}{' '}
                          {
                            <CampaignIdBadge
                              isDisabled={
                                isAttributionModesDisabled || isAttributionRadioButtonDisabled
                              }
                            />
                          }{' '}
                          {ATTRIBUTE_MODE_MAP_JOURNEY_FEATURE_REFACTOR.ExplicitlySet.suffix}
                        </RadioLabel>
                      </RadioOption>
                    </Tooltip>
                  </FlexColumnHalfGapFormSectionContent>
                </HalfPaddedFormSectionContent>
              </>
            ) : (
              /**
               * Once the JourneyGoals feature flag is GA'd, remove the code below and the components that we don't need since we have inlined them.
               */
              <>
                <MultiConversionRadios
                  value={mode}
                  onChange={handleAttributionsModeUpdate}
                  isDisabled={isDisabled}
                />
                {mode === AttributionMode.AttributionPeriod && (
                  <MultiConversionPeriodInput
                    value={period}
                    onChange={handlePeriodUpdate}
                    isDisabled={isDisabled}
                  />
                )}
              </>
            )
          }
        </>
      )}
    </HalfPaddedFormSectionContent>
  );
};

export default MultipleConversionSelection;
